import { useState, useEffect, useRef, useMemo } from 'react';

export default function usePropFirmChallenge(session, equity, startingCapital, allCandles, currentIndex) {
  const challengeConfig = session?.challenge_config;
  const isChallenge = !!challengeConfig;

  const [status, setStatus] = useState(session?.challenge_status || 'active');
  const [violation, setViolation] = useState(session?.challenge_violation || null);
  const peakEquityRef = useRef(startingCapital || 0);
  const dailyBalanceStartRef = useRef({}); // { "YYYY-MM-DD": balanceAtDayStart }
  const highestEndOfDayBalanceRef = useRef(startingCapital || 0); // for 1-step trailing
  const tradingDaysRef = useRef(new Set(session?.challenge_trading_days || []));
  const lastDateRef = useRef(null);

  // Reset on session change
  useEffect(() => {
    if (!isChallenge) return;
    peakEquityRef.current = parseFloat(session?.challenge_peak_equity || startingCapital || 0);
    setStatus(session?.challenge_status || 'active');
    setViolation(session?.challenge_violation || null);
    tradingDaysRef.current = new Set(session?.challenge_trading_days || []);
  }, [session?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check rules on each bar
  useEffect(() => {
    if (!isChallenge || !equity || status !== 'active' || !allCandles?.length || currentIndex <= 0) return;

    const bar = allCandles[currentIndex - 1];
    if (!bar) return;

    const currentEquity = equity;
    const capital = parseFloat(startingCapital);
    const config = challengeConfig;

    // Get current date from bar
    const barDate = new Date(bar.time * 1000).toISOString().split('T')[0];

    // Track new day: record balance (not equity) and update trailing floor for 1-step
    if (barDate !== lastDateRef.current) {
      // End of previous day: update highest end-of-day balance for 1-step trailing
      if (lastDateRef.current && config.challenge_type === '1step') {
        const prevDayBalance = currentEquity; // approx: equity at day boundary
        if (prevDayBalance > highestEndOfDayBalanceRef.current) {
          highestEndOfDayBalanceRef.current = prevDayBalance;
        }
      }
      // Start of new day: record balance at day start (FTMO uses balance, not equity)
      dailyBalanceStartRef.current[barDate] = currentEquity;
      lastDateRef.current = barDate;
    }

    // Update peak equity
    if (currentEquity > peakEquityRef.current) {
      peakEquityRef.current = currentEquity;
    }

    // ═══ FTMO DAILY LOSS (MDL) ═══
    // "The result of all closed and open positions on any given day must not exceed the MDL"
    // MDL is calculated from the BALANCE at start of day (recalculated at midnight CET)
    // MDL = initial_balance * daily_loss_limit_pct%
    const dayStartBalance = dailyBalanceStartRef.current[barDate] || capital;
    const todayLoss = dayStartBalance - currentEquity;
    const dailyLossLimit = capital * parseFloat(config.daily_loss_limit_pct) / 100;

    if (todayLoss > dailyLossLimit) {
      setStatus('failed');
      setViolation(`Daily loss limit exceeded: -$${todayLoss.toFixed(2)} (limit: -$${dailyLossLimit.toFixed(2)})`);
      return;
    }

    // ═══ FTMO MAX LOSS (ML) ═══
    // 2-Step: Fixed — equity must never fall below (initial_balance - ML)
    // 1-Step: End-of-Day Trailing — floor = highest_end_of_day_balance - ML
    //   (floor only moves UP, never down)
    const maxLossLimit = capital * parseFloat(config.max_drawdown_pct) / 100;
    let drawdownFloor;

    if (config.challenge_type === '1step') {
      // 1-Step trailing: floor based on highest end-of-day balance
      drawdownFloor = highestEndOfDayBalanceRef.current - maxLossLimit;
    } else {
      // 2-Step: fixed floor from initial balance
      drawdownFloor = capital - maxLossLimit;
    }

    if (currentEquity < drawdownFloor) {
      const currentDrawdown = (config.challenge_type === '1step' ? highestEndOfDayBalanceRef.current : capital) - currentEquity;
      setStatus('failed');
      setViolation(`Max loss exceeded: equity $${currentEquity.toFixed(2)} below floor $${drawdownFloor.toFixed(2)} (limit: -$${maxLossLimit.toFixed(2)})`);
      return;
    }
  }, [isChallenge, equity, currentIndex, allCandles, status, challengeConfig, startingCapital]);

  // Computed values
  const computed = useMemo(() => {
    if (!isChallenge || !challengeConfig) {
      return { isChallenge: false };
    }

    const capital = parseFloat(startingCapital);
    const config = challengeConfig;
    const currentEquity = equity || capital;

    // Profit
    const profitTarget = capital * parseFloat(config.profit_target_pct) / 100;
    const currentProfit = currentEquity - capital;
    const profitPercent = (currentProfit / capital) * 100;

    // Daily loss (from balance at start of day)
    const bar = allCandles?.[currentIndex - 1];
    const barDate = bar ? new Date(bar.time * 1000).toISOString().split('T')[0] : null;
    const dayStartBalance = barDate ? (dailyBalanceStartRef.current[barDate] || capital) : capital;
    const todayPnl = currentEquity - dayStartBalance;
    const dailyLossLimit = capital * parseFloat(config.daily_loss_limit_pct) / 100;
    const todayPnlPercent = (todayPnl / capital) * 100;

    // Max Loss
    const maxLossLimit = capital * parseFloat(config.max_drawdown_pct) / 100;
    const mlRef = config.challenge_type === '1step' ? highestEndOfDayBalanceRef.current : capital;
    const drawdownFloor = mlRef - maxLossLimit;
    const currentDrawdown = Math.max(0, mlRef - currentEquity);
    const drawdownPercent = (currentDrawdown / capital) * 100;

    // Trading days
    const tradingDays = tradingDaysRef.current.size;

    return {
      isChallenge: true,
      status,
      violation,
      // Profit
      profitTarget,
      profitTargetPct: parseFloat(config.profit_target_pct),
      currentProfit: Math.round(currentProfit * 100) / 100,
      profitPercent: Math.round(profitPercent * 100) / 100,
      // Daily loss
      todayPnl: Math.round(todayPnl * 100) / 100,
      todayPnlPercent: Math.round(todayPnlPercent * 100) / 100,
      dailyLossLimit,
      dailyLossLimitPct: parseFloat(config.daily_loss_limit_pct),
      // Drawdown
      currentDrawdown: Math.round(currentDrawdown * 100) / 100,
      drawdownPercent: Math.round(drawdownPercent * 100) / 100,
      maxDrawdownLimit: maxLossLimit,
      maxDrawdownPct: parseFloat(config.max_drawdown_pct),
      // Trading days
      tradingDays,
      minTradingDays: config.min_trading_days,
      maxCalendarDays: config.max_calendar_days,
      // Config
      leverage: config.leverage,
      accountType: config.account_type,
      challengeType: config.challenge_type,
      firmName: config.firm,
      challengeName: config.name,
    };
  }, [isChallenge, challengeConfig, equity, startingCapital, status, violation, allCandles, currentIndex]);

  // Track trading days when trades are opened
  const recordTradingDay = (timestamp) => {
    if (!isChallenge) return;
    const date = new Date(timestamp).toISOString().split('T')[0];
    tradingDaysRef.current.add(date);
  };

  // Check if challenge is passed
  const checkPassed = () => {
    if (!isChallenge || status !== 'active') return false;
    const capital = parseFloat(startingCapital);
    const config = challengeConfig;
    const currentProfit = (equity || capital) - capital;
    const profitTarget = capital * parseFloat(config.profit_target_pct) / 100;
    const tradingDays = tradingDaysRef.current.size;

    if (currentProfit >= profitTarget && tradingDays >= config.min_trading_days) {
      setStatus('passed');
      return true;
    }
    if (currentProfit >= profitTarget && tradingDays < config.min_trading_days) {
      setViolation(`Profit target reached but minimum trading days not met (${tradingDays}/${config.min_trading_days})`);
      return false;
    }
    return false;
  };

  return { ...computed, recordTradingDay, checkPassed };
}
